/**
 * LeaderReviewResults.tsx
 * Results page — visible to ALL enrolled leaders + admin after publish.
 *
 * Admin view: consolidation panel (save + publish results)
 * All leaders: group scores grid + individual breakdown
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchSession, fetchEnrollments, fetchAllResults,
  fetchRatingsForLeader, fetchLiveNotes,
  saveConsolidatedResult, publishResults,
  aggregatePeerScores, computeConsolidatedScore,
} from '../services/leaderReviewService';
import {
  fetchSelfAssessment as fetchSelf,
} from '../services/leaderReviewService';
import { LEADER_MUSCLES } from '../types';
import type {
  LeaderReviewSession, LeaderReviewEnrollment, LeaderReviewResult,
} from '../types';

// ─── Score cell ───────────────────────────────────────────────────────────────

function ScoreCell({ score, highlight = false }: { score: number; highlight?: boolean }) {
  if (!score) return <span className="text-gray-300 text-xs">—</span>;
  const color = score >= 4 ? 'text-emerald-600' : score >= 3 ? 'text-amber-600' : 'text-red-500';
  return (
    <span className={`text-sm font-bold ${highlight ? color : 'text-gray-700'}`}>
      {score.toFixed(1)}
    </span>
  );
}

// ─── Dot bar ──────────────────────────────────────────────────────────────────

function DotBar({ score }: { score: number }) {
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(n => (
        <div
          key={n}
          className={`w-2 h-2 rounded-full ${n <= Math.round(score) ? 'bg-[#011132]' : 'bg-gray-100'}`}
        />
      ))}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

const LeaderReviewResults: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { user }      = useAuth();
  const navigate      = useNavigate();

  const isHR = ['superadmin', 'admin', 'hr_admin'].includes(user?.role ?? '');

  const [session, setSession]           = useState<LeaderReviewSession | null>(null);
  const [enrollments, setEnrollments]   = useState<LeaderReviewEnrollment[]>([]);
  const [results, setResults]           = useState<LeaderReviewResult[]>([]);
  const [loading, setLoading]           = useState(true);
  const [publishing, setPublishing]     = useState(false);
  const [selectedLeaderId, setSelectedLeaderId] = useState<string | null>(null);

  // Consolidation state (admin)
  const [consolidating, setConsolidating] = useState(false);
  const [consolidationData, setConsolidationData] = useState<Record<string, {
    discretionAdj: number;
    discretionReason: string;
    facilitatorNotes: string;
    devRecommendations: string;
    liveScore: number | null;
  }>>({});

  const load = useCallback(async () => {
    if (!sessionId) return;
    setLoading(true);
    try {
      const [sess, enrolls, allResults] = await Promise.all([
        fetchSession(sessionId),
        fetchEnrollments(sessionId),
        fetchAllResults(sessionId),
      ]);
      setSession(sess);
      setEnrollments(enrolls);
      setResults(allResults);

      // Init consolidation data
      const initData: typeof consolidationData = {};
      for (const e of enrolls) {
        const existing = allResults.find(r => r.leaderId === e.leaderId);
        initData[e.leaderId] = {
          discretionAdj: existing?.discretionAdjustment ?? 0,
          discretionReason: existing?.discretionReason ?? '',
          facilitatorNotes: existing?.facilitatorNotes ?? '',
          devRecommendations: existing?.developmentRecommendations ?? '',
          liveScore: existing?.liveScore ?? null,
        };
      }
      setConsolidationData(initData);
    } finally {
      setLoading(false);
    }
  }, [sessionId]);

  useEffect(() => { load(); }, [load]);

  // ── Auto-compute + save all results ──────────────────────────────────────

  const handleSaveAll = async () => {
    if (!session || !sessionId) return;
    setConsolidating(true);
    try {
      for (const e of enrollments) {
        const [selfData, ratings, liveNotesList] = await Promise.all([
          fetchSelf(sessionId, e.leaderId),
          fetchRatingsForLeader(sessionId, e.leaderId),
          fetchLiveNotes(sessionId, e.leaderId),
        ]);
        const selfScores    = selfData?.scores ?? [];
        const peerAvgScores = aggregatePeerScores(ratings);
        const cd            = consolidationData[e.leaderId];
        const liveScore     = cd?.liveScore ?? (liveNotesList.find(n => n.liveScore !== undefined)?.liveScore);

        const consolidated = computeConsolidatedScore(
          peerAvgScores, selfScores, session.scoreWeights, liveScore ?? undefined,
        );
        const finalScore = Math.max(1, Math.min(5, consolidated + (cd?.discretionAdj ?? 0)));

        await saveConsolidatedResult({
          sessionId,
          leaderId: e.leaderId,
          leaderName: e.leaderName,
          krDeliveryPct: e.krDeliveryPct,
          selfScores,
          peerAvgScores,
          liveScore: liveScore ?? undefined,
          consolidatedScore: consolidated,
          discretionAdjustment: cd?.discretionAdj ?? 0,
          discretionReason: cd?.discretionReason ?? '',
          finalScore,
          facilitatorNotes: cd?.facilitatorNotes ?? '',
          developmentRecommendations: cd?.devRecommendations ?? '',
        });
      }
      await load();
    } finally {
      setConsolidating(false);
    }
  };

  const handlePublish = async () => {
    if (!sessionId || !session) return;
    if (!confirm('Publish results? All enrolled leaders will be able to see scores.')) return;
    setPublishing(true);
    try {
      await publishResults(sessionId);
      await load();
    } finally {
      setPublishing(false);
    }
  };

  // ── My result ─────────────────────────────────────────────────────────────

  const myResult      = results.find(r => r.leaderId === user?.uid);
  const selectedResult = selectedLeaderId ? results.find(r => r.leaderId === selectedLeaderId) : null;
  const displayResult  = selectedResult ?? myResult;
  const displayName    = selectedLeaderId
    ? enrollments.find(e => e.leaderId === selectedLeaderId)?.leaderName ?? '—'
    : 'My Results';

  const isPublished   = session?.status === 'completed';
  const canView       = isPublished || isHR;

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-5 h-5 border-2 border-[#011132] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!canView) return (
    <div className="max-w-lg mx-auto px-6 py-16 text-center">
      <span className="material-symbols-rounded text-4xl text-gray-300">lock</span>
      <p className="text-gray-500 mt-3 text-sm">Results will be available after the live session and admin consolidation.</p>
      <button onClick={() => navigate('/leader-review')} className="mt-4 text-sm text-[#011132] hover:underline">← Back</button>
    </div>
  );

  return (
    <div className="max-w-6xl mx-auto px-6 py-8 space-y-8">

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/leader-review')} className="text-gray-400 hover:text-gray-600">
            <span className="material-symbols-rounded" style={{ fontSize: 20 }}>arrow_back</span>
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">{session?.name} — Results</h1>
            <p className="text-sm text-gray-400">
              {isPublished ? 'Published · visible to all enrolled leaders' : 'Consolidation in progress'}
            </p>
          </div>
        </div>
        {isHR && !isPublished && (
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveAll}
              disabled={consolidating}
              className="text-sm font-medium text-gray-600 border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              {consolidating ? 'Computing…' : 'Compute & Save All'}
            </button>
            <button
              onClick={handlePublish}
              disabled={publishing || results.length === 0}
              className="bg-[#011132] text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-[#0a2254] disabled:opacity-50 transition-colors"
            >
              {publishing ? 'Publishing…' : 'Publish Results'}
            </button>
          </div>
        )}
      </div>

      {/* Group scores grid — all leaders */}
      <section>
        <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">Group Overview</h2>
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-40">Leader</th>
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">KR %</th>
                  {LEADER_MUSCLES.map(m => (
                    <th key={m.key} className="text-center px-2 py-3 text-[10px] font-semibold text-gray-400 uppercase tracking-wider w-12" title={m.name}>
                      {m.name.split(' ').map(w => w[0]).join('')}
                    </th>
                  ))}
                  <th className="text-center px-3 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider w-16">Final</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {enrollments.map(e => {
                  const r         = results.find(res => res.leaderId === e.leaderId);
                  const isMe      = e.leaderId === user?.uid;
                  const isSelected = selectedLeaderId === e.leaderId;
                  return (
                    <tr
                      key={e.id}
                      className={`cursor-pointer transition-colors ${isSelected ? 'bg-[#011132]/5' : isMe ? 'bg-amber-50/50' : 'hover:bg-gray-50'}`}
                      onClick={() => setSelectedLeaderId(prev => prev === e.leaderId ? null : e.leaderId)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-[#011132]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[10px] font-bold text-[#011132]">{e.leaderName[0]}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900 text-xs truncate">{e.leaderName}</p>
                            {isMe && <span className="text-[9px] text-amber-600 font-medium">You</span>}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className={`text-xs font-bold ${e.krDeliveryPct >= 80 ? 'text-emerald-600' : e.krDeliveryPct >= 50 ? 'text-amber-500' : 'text-red-400'}`}>
                          {e.krDeliveryPct}%
                        </span>
                      </td>
                      {LEADER_MUSCLES.map(m => {
                        const peerScore = r?.peerAvgScores.find(s => s.muscleKey === m.key)?.score ?? 0;
                        return (
                          <td key={m.key} className="px-2 py-3 text-center">
                            <ScoreCell score={peerScore} highlight />
                          </td>
                        );
                      })}
                      <td className="px-3 py-3 text-center">
                        {r?.finalScore ? (
                          <span className={`text-sm font-bold ${r.finalScore >= 4 ? 'text-emerald-600' : r.finalScore >= 3 ? 'text-amber-500' : 'text-red-500'}`}>
                            {r.finalScore.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-3 text-[10px] text-gray-400">
            {LEADER_MUSCLES.map(m => (
              <span key={m.key} className="flex items-center gap-0.5">
                <strong>{m.name.split(' ').map(w => w[0]).join('')}</strong>={m.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Individual breakdown */}
      {displayResult && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            {selectedLeaderId && selectedLeaderId !== user?.uid ? `${displayName} — Detail` : 'My Breakdown'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Self vs Peer per muscle */}
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Score by Muscle</p>
              <div className="space-y-3">
                {LEADER_MUSCLES.map(m => {
                  const self = displayResult.selfScores.find(s => s.muscleKey === m.key)?.score ?? 0;
                  const peer = displayResult.peerAvgScores.find(s => s.muscleKey === m.key)?.score ?? 0;
                  return (
                    <div key={m.key} className="flex items-center gap-3">
                      <span className="text-xs text-gray-600 w-28 flex-shrink-0">{m.name}</span>
                      <div className="flex-1 grid grid-cols-2 gap-2">
                        <div className="flex items-center gap-1.5">
                          <DotBar score={self} />
                          <span className="text-xs text-gray-400">{self ? self.toFixed(1) : '—'}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="flex gap-0.5">
                            {[1,2,3,4,5].map(n => (
                              <div key={n} className={`w-2 h-2 rounded-full ${n <= Math.round(peer) ? 'bg-blue-400' : 'bg-gray-100'}`} />
                            ))}
                          </div>
                          <span className="text-xs text-gray-400">{peer ? peer.toFixed(1) : '—'}</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center gap-4 text-xs text-gray-400 border-t border-gray-100 pt-3">
                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-[#011132]" />Self</span>
                <span className="flex items-center gap-1"><div className="w-2 h-2 rounded-full bg-blue-400" />Peers</span>
              </div>
            </div>

            {/* Summary card */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Summary</p>

              <div className="grid grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">KR Delivery</p>
                  <p className={`text-2xl font-bold ${displayResult.krDeliveryPct >= 80 ? 'text-emerald-600' : displayResult.krDeliveryPct >= 50 ? 'text-amber-500' : 'text-red-500'}`}>
                    {displayResult.krDeliveryPct}%
                  </p>
                </div>
                <div className="bg-[#011132]/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-gray-400 mb-1">Behavioral Score</p>
                  <p className={`text-2xl font-bold ${displayResult.finalScore >= 4 ? 'text-emerald-600' : displayResult.finalScore >= 3 ? 'text-amber-500' : 'text-red-500'}`}>
                    {displayResult.finalScore.toFixed(1)}<span className="text-sm font-normal text-gray-400">/5</span>
                  </p>
                </div>
              </div>

              {displayResult.facilitatorNotes && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Facilitator Notes</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{displayResult.facilitatorNotes}</p>
                </div>
              )}
              {displayResult.developmentRecommendations && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">Development Focus</p>
                  <p className="text-sm text-gray-600 leading-relaxed">{displayResult.developmentRecommendations}</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Admin consolidation panel */}
      {isHR && !isPublished && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
            Consolidation — Discretion & Notes
          </h2>
          <div className="space-y-3">
            {enrollments.map(e => {
              const cd = consolidationData[e.leaderId] ?? { discretionAdj: 0, discretionReason: '', facilitatorNotes: '', devRecommendations: '', liveScore: null };
              const update = (key: string, val: string | number | null) => {
                setConsolidationData(prev => ({
                  ...prev,
                  [e.leaderId]: { ...prev[e.leaderId], [key]: val },
                }));
              };
              return (
                <div key={e.id} className="bg-white border border-gray-200 rounded-xl p-5">
                  <div className="flex items-center justify-between gap-4 mb-4">
                    <div>
                      <h3 className="font-bold text-gray-900">{e.leaderName}</h3>
                      <p className="text-xs text-gray-400">{e.leaderJobTitle}</p>
                    </div>
                    <div className="text-right text-xs text-gray-400">
                      <div>Live score override</div>
                      <div className="flex gap-1 mt-1">
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => update('liveScore', cd.liveScore === n ? null : n)}
                            className={`w-7 h-7 rounded-full text-xs font-bold border transition-all
                              ${cd.liveScore === n ? 'bg-[#011132] border-[#011132] text-white' : 'border-gray-200 text-gray-400 hover:border-gray-400'}`}
                          >{n}</button>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Discretion Adjustment</label>
                      <div className="flex items-center gap-2">
                        <input type="number" step={0.1} min={-1} max={1}
                          className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-center"
                          value={cd.discretionAdj}
                          onChange={e2 => update('discretionAdj', Number(e2.target.value))}
                        />
                        <span className="text-xs text-gray-400">(-1.0 to +1.0)</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Reason</label>
                      <input className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm"
                        placeholder="Brief reason for adjustment…"
                        value={cd.discretionReason}
                        onChange={e2 => update('discretionReason', e2.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Facilitator Notes</label>
                      <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm resize-none"
                        placeholder="Session observations…"
                        value={cd.facilitatorNotes}
                        onChange={e2 => update('facilitatorNotes', e2.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">Development Recommendations</label>
                      <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm resize-none"
                        placeholder="Growth focus for next period…"
                        value={cd.devRecommendations}
                        onChange={e2 => update('devRecommendations', e2.target.value)}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
};

export default LeaderReviewResults;
